import { useMemo, useState } from 'react';

import ArrowDown from '~/assets/icons/triangle--down.svg';
import { cn } from '~/lib/utils/classname';

export type ClassNameSlots = 'main' | 'label' | 'input';

export type SelectOption = Omit<
  React.OptionHTMLAttributes<HTMLOptionElement>,
  'value' | 'children'
> & {
  value: string;
};

export type SelectOptGroup = {
  label: string;
  children: SelectOption[];
};

export type SelectItem = SelectOption | SelectOptGroup;

type Props = {
  label?: string;
  placeholder?: string;
  className?: string;
  classNames?: { [key in ClassNameSlots]?: string };
  options: SelectItem[];
  withLabelValue?: boolean;
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'>;

const isOptGroup = (item: SelectItem): item is SelectOptGroup =>
  Boolean(item && 'children' in item);

const FormSelect: React.FC<Props> = ({
  label,
  className,
  classNames,
  placeholder,
  options,
  withLabelValue,
  ...inputProps
}) => {
  const isControlled = inputProps.value !== undefined;

  const [internalValue, setInternalValue] = useState<string>(() => {
    const defaultValue = inputProps.defaultValue;
    return typeof defaultValue === 'string' ? defaultValue : '';
  });

  const selectValue = (isControlled ? inputProps.value : internalValue) ?? '';

  const flatOptions = useMemo(() => {
    return options.reduce((acc, item) => {
      if (isOptGroup(item)) {
        return [...acc, ...item.children];
      }
      return [...acc, item];
    }, [] as SelectOption[]);
  }, [options]);

  const currentLabel = useMemo(() => {
    return flatOptions.find((option) => option.value === String(selectValue))
      ?.label;
  }, [flatOptions, selectValue]);

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    inputProps.onChange?.(event);

    if (!isControlled) {
      setInternalValue(event.currentTarget.value);
    }
  };

  return (
    <label
      htmlFor={inputProps.name}
      className={cn(
        'flex w-full flex-col transition-opacity',
        { 'pointer-events-none opacity-50': inputProps.disabled },
        className,
        classNames?.main
      )}
    >
      {label && (
        <span
          className={cn(
            'text-brand-primary relative block px-6 py-2 text-base',
            classNames?.label
          )}
        >
          {label}
          {inputProps.required && <span className="text-danger">&nbsp;*</span>}
        </span>
      )}

      <div className="relative flex">
        <select
          {...inputProps}
          className={cn(
            'bg-brand-quaternary placeholder:text-brand-primary border-brand-primary required:invalid:text-brand-primary h-16 grow appearance-none border-b px-6 text-white outline-none',
            classNames?.input
          )}
          value={String(selectValue)}
          onChange={handleChange}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}

          {options.map((item) => {
            if (isOptGroup(item)) {
              const { label, children } = item;

              return (
                <optgroup key={label} label={label}>
                  {children.map(({ value, label, ...rest }) => (
                    <option
                      key={value}
                      className="text-white"
                      value={value}
                      {...rest}
                    >
                      {label}
                    </option>
                  ))}
                </optgroup>
              );
            }

            const { value, label, ...rest } = item;

            return (
              <option
                key={value}
                className="text-white"
                value={value}
                {...rest}
              >
                {label}
              </option>
            );
          })}
        </select>

        <div className="pointer-events-none absolute top-0 right-0 flex aspect-square h-full items-center justify-center">
          <ArrowDown className="text-brand-tertiary relative h-8" />
        </div>
      </div>

      {withLabelValue && (
        <input
          type="hidden"
          name={[inputProps.name, 'label'].join('__')}
          value={currentLabel ?? ''}
        />
      )}
    </label>
  );
};

export default FormSelect;
